import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { getUpdateSettings, updateUpdateSettings } from '../../services/deviceAppUpdateService';

export default function UpdateSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [startUtc, setStartUtc] = useState('02:00');
  const [endUtc, setEndUtc] = useState('05:00');

  const getRomeTimeLabel = (utcHHMM) => {
    try {
      if (!utcHHMM || typeof utcHHMM !== 'string' || !utcHHMM.includes(':')) return '';
      const [hhRaw, mmRaw] = utcHHMM.split(':');
      const hh = Number(hhRaw);
      const mm = Number(mmRaw);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return '';

      // Anchor to today's UTC date so DST for Europe/Rome is applied correctly.
      const now = new Date();
      const utcDate = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        hh,
        mm,
        0,
        0
      ));

      const timeFmt = new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      const dateFmt = new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      const romeTime = timeFmt.format(utcDate);
      const romeDate = dateFmt.format(utcDate);

      // Compute if Rome local date differs from the UTC date we anchored on.
      const utcDayKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
      const [d, m, y] = romeDate.split('/');
      const romeDayKey = `${y}-${m}-${d}`;

      let dayShift = '';
      if (romeDayKey > utcDayKey) dayShift = ' (+1g)';
      else if (romeDayKey < utcDayKey) dayShift = ' (-1g)';

      return `${romeTime}${dayShift}`;
    } catch {
      return '';
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotice(null);

      const data = await getUpdateSettings();
      const mw = data?.maintenanceWindow;

      setEnabled(!!mw?.enabled);
      setStartUtc(mw?.startUtc || '02:00');
      setEndUtc(mw?.endUtc || '05:00');
    } catch (err) {
      setError(err.message);
      setNotice({ severity: 'error', message: err.message || 'Errore caricamento impostazioni' });
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleEnabledToggle = async (nextEnabled) => {
    // Optimistic UI update + auto-save
    setEnabled(nextEnabled);

    // Avoid auto-saving while initial load is still running
    if (loading) return;
    if (saving) return;

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      const payload = {
        maintenanceWindow: {
          enabled: nextEnabled,
          startUtc,
          endUtc,
        },
      };

      await updateUpdateSettings(payload);
      setNotice({
        severity: nextEnabled ? 'success' : 'info',
        message: nextEnabled ? 'Maintenance window attivata' : 'Maintenance window disattivata',
      });
      setSnackbarOpen(true);
    } catch (err) {
      // Roll back UI state on failure
      setEnabled((prev) => !prev);
      setError(err.message);
      setNotice({ severity: 'error', message: err.message || 'Errore durante il salvataggio' });
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      const payload = {
        maintenanceWindow: {
          enabled,
          startUtc,
          endUtc,
        },
      };

      await updateUpdateSettings(payload);
      setNotice({ severity: 'success', message: 'Finestra temporale salvata' });
      setSnackbarOpen(true);
      await load();
    } catch (err) {
      setError(err.message);
      setNotice({ severity: 'error', message: err.message || 'Errore durante il salvataggio' });
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const disabledText = enabled
    ? 'Attiva: tutti i device installeranno aggiornamenti SOLO dentro la finestra UTC.'
    : 'Disattiva: la maintenance window non viene applicata (installAllowedNow = true).';

  return (
    <Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Alert
            severity={notice.severity}
            variant="filled"
            onClose={() => setSnackbarOpen(false)}
            sx={{ width: '100%' }}
          >
            {notice.message}
          </Alert>
        ) : (
          <Alert severity="info" variant="filled" sx={{ width: '100%' }}>
            Finestra temporale salvata
          </Alert>
        )}
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ⚙️ Configurazioni Globali Update
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La maintenance window è valutata dal backend in UTC (clock server) e vale per tutti i device.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => handleEnabledToggle(e.target.checked)}
              disabled={loading || saving}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span>Maintenance window (UTC)</span>
              <Chip
                size="small"
                icon={enabled ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />}
                label={enabled ? 'Attiva' : 'Disattivata'}
                color={enabled ? 'success' : 'default'}
                variant={enabled ? 'filled' : 'outlined'}
              />
            </Box>
          }
        />

        <Alert severity={enabled ? 'warning' : 'info'} sx={{ mt: 1, mb: 2 }}>
          {disabledText}
          <br />
          Nota: i check forzati (Force Check da WebSocket/admin kiosk) <strong>bypassano la maintenance window</strong> e installano sempre quando disponibili.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Inizio (UTC)"
            type="time"
            value={startUtc}
            onChange={(e) => setStartUtc(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            disabled={loading}
          />

          <TextField
            label="Fine (UTC)"
            type="time"
            value={endUtc}
            onChange={(e) => setEndUtc(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            disabled={loading}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Ora di Roma (Europe/Rome, DST incluso):
          <br />
          - Inizio: {getRomeTimeLabel(startUtc) || '—'}
          <br />
          - Fine: {getRomeTimeLabel(endUtc) || '—'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Come funziona:
          <br />
          - Il kiosk continua a fare i check (polling/boot/force).
          <br />
          - Il backend risponde con updateAvailable e installAllowedNow.
          <br />
          - Fuori finestra: installAllowedNow=false → il kiosk non installa e non apre l’overlay.
        </Typography>
      </Paper>
    </Box>
  );
}
